import React, { ReactElement, useCallback, useState } from 'react';
import { FeedbackButtonType, QuickFeedback, ThumbType } from '@atlassian/learning-components';
import { useLoaderData } from 'react-router-dom';

import { Opinion } from 'src/services/http/quickFeedback';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendUIEvent } from 'src/utils/analytics';
import { ArticleLoaderProps } from 'src/pages/ArticlePage';

interface QuickFeedbackForArticleProps {
  shouldShowDivider?: boolean;
}

export const QuickFeedbackForArticle = ({ shouldShowDivider }: QuickFeedbackForArticleProps): ReactElement => {
  const { articleData } = useLoaderData() as ArticleLoaderProps;

  const { user } = useAtlassianUser();

  const [feedbackState, setFeedbackState] = useState({
    opinion: '',
    thoughts: '',
    selectedOption: '',
  });

  const handleChangeThoughts = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFeedbackState({ ...feedbackState, thoughts: e.target.value });
    },
    [feedbackState],
  );

  const handleChangeOption = useCallback(
    (option: string) => {
      setFeedbackState({ ...feedbackState, selectedOption: option });
    },
    [feedbackState],
  );

  const handleThumbClick = useCallback(
    (thumbType: ThumbType) => {
      setFeedbackState({
        ...feedbackState,
        opinion: thumbType === ThumbType.THUMB_UP ? Opinion.SATISFIED : Opinion.NOT_SATISFIED,
      });
    },
    [feedbackState],
  );

  const handleSubmit = useCallback(
    (buttonType: FeedbackButtonType) => {
      sendUIEvent(
        'articleScreen',
        'button',
        'clicked',
        buttonType === FeedbackButtonType.SUBMIT ? 'submitFeedbackButton' : 'dismissFeedbackButton',
        {
          thumbType: feedbackState.opinion === Opinion.SATISFIED ? ThumbType.THUMB_UP : ThumbType.THUMB_DOWN,
          isThoughtsFieldEmpty: !feedbackState.thoughts.length,
          thoughts: feedbackState.thoughts,
          selectedOption: feedbackState.selectedOption,
          article: articleData?.title || '',
        },
        user?.account_id,
      );
    },
    [articleData?.title, user?.account_id, feedbackState],
  );

  return (
    <QuickFeedback
      onSubmitFeedback={handleSubmit}
      onChangeThoughts={handleChangeThoughts}
      onChangeOption={handleChangeOption}
      onThumbClick={handleThumbClick}
      shouldShowDivider={shouldShowDivider}
      buttonText="Was this page useful?"
    />
  );
};
